// Entry point for the build script in your package.json
import "@hotwired/turbo-rails"
import "./controllers"
import * as ActiveStorage from "@rails/activestorage"
import { t } from "./i18n"

// Trix/ActionText JS removed — Lexical is now used for all rich-text editing.
// The trix npm package is kept only for its CSS (display of existing content).

// Start ActiveStorage
ActiveStorage.start()

// ─── Lucide Icons ─────────────────────────────────────────────────────────────
// Icons are bundled by esbuild (cached) — no CDN request at runtime.
// createIcons() scans the DOM for <i data-lucide="icon-name"> elements and
// replaces them with inline SVGs. Re-run after every Turbo navigation.
import {
  createIcons,
  Package, ClipboardList, Calendar, ArrowLeftRight, Wrench,
  QrCode, Download, Truck, CalendarCheck, LayoutTemplate, Boxes,
  List, Globe, Bot, Scale, ListChecks, PenLine, ShieldCheck,
  Users, UsersRound, Mail, Inbox, Building2, CreditCard,
  ChevronDown, CheckCircle2, Circle, Plus, Menu, X, User, LogOut,
  Cable, Pencil, Unlink, Link2, ExternalLink, TriangleAlert,
  CirclePlus, CircleMinus, ShieldAlert, Network, LayoutGrid,
  Eye, EyeOff, Maximize2, Minimize2,
  MapPin, Sparkles, LayoutDashboard, DownloadCloud,
  HeartPulse, Radio, Gauge, Brain,
  Router, Wifi, Navigation, Fence, Map,
  AlertCircle, Lock, Unlock, Activity, BellRing, Cpu, Database,
  DollarSign, Send, ShieldX, TrendingUp, Waves, Zap, Box,
  Container, Plane, BookOpen, Settings, ClipboardCheck, AlertOctagon,
  Award, Anchor, CheckSquare, Hammer, Shield, TrainTrack, GitBranch,
  BarChart2, AlertTriangle, FileText, Link, Scissors, Trash2, Star
} from "lucide"

const lucideIcons = {
  Package, ClipboardList, Calendar, ArrowLeftRight, Wrench,
  QrCode, Download, Truck, CalendarCheck, LayoutTemplate, Boxes,
  List, Globe, Bot, Scale, ListChecks, PenLine, ShieldCheck,
  Users, UsersRound, Mail, Inbox, Building2, CreditCard,
  ChevronDown, CheckCircle2, Circle, Plus, Menu, X, User, LogOut,
  Cable, Pencil, Unlink, Link2, ExternalLink, TriangleAlert,
  CirclePlus, CircleMinus, ShieldAlert, Network, LayoutGrid,
  Eye, EyeOff, Maximize2, Minimize2,
  MapPin, Sparkles, LayoutDashboard, DownloadCloud,
  HeartPulse, Radio, Gauge, Brain,
  Router, Wifi, Navigation, Fence, Map,
  AlertCircle, Lock, Unlock, Activity, BellRing, Cpu, Database,
  DollarSign, Send, ShieldX, TrendingUp, Waves, Zap, Box,
  Container, Plane, BookOpen, Settings, ClipboardCheck, AlertOctagon,
  Award, Anchor, CheckSquare, Hammer, Shield, TrainTrack, GitBranch,
  BarChart2, AlertTriangle, FileText, Link, Scissors, Trash2, Star
}

function initLucide() { createIcons({ icons: lucideIcons }) }

document.addEventListener("DOMContentLoaded", initLucide)
document.addEventListener("turbo:load", initLucide)
document.addEventListener("turbo:render", initLucide)

// ─── Global form submit spinner ───────────────────────────────────────────────
// Automatically adds a loading spinner to any submit button when its form is
// submitted.  Forms that already have a dedicated Stimulus controller handling
// spinners should set data-no-global-spinner="true" on the <form> element, or
// their controller name should be listed in SPINNER_SKIP_CONTROLLERS below.
//
// Individual buttons can customise the loading label via
// data-loading-text="Sending…" on the button element.

const SPINNER_SVG = `<svg class="animate-spin -ml-1 mr-2 h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>`;
const SPINNER_SKIP_CONTROLLERS = ["compliance-upload", "file-upload-spinner"];

function shouldSkipSpinner(form) {
  if (!form) return false;
  if (form.dataset.noGlobalSpinner === "true") return true;
  const ctrl = form.dataset.controller || "";
  return SPINNER_SKIP_CONTROLLERS.some((c) => ctrl.includes(c));
}

function spinButton(btn) {
  if (!btn || btn.dataset.globalSpinning) return;
  btn.dataset.globalSpinning = "true";
  btn.dataset.globalOriginalHtml = btn.innerHTML;
  btn.dataset.globalOriginalValue = btn.value || "";
  btn.disabled = true;
  const label =
    btn.dataset.loadingText ||
    btn.innerText?.trim() ||
    btn.value ||
    t("javascript.application.processing");
  if (btn.tagName === "INPUT") {
    btn.value = btn.dataset.loadingText || t("javascript.application.processing");
  } else {
    btn.innerHTML = `${SPINNER_SVG} ${label}`;
  }
}

function restoreButton(btn) {
  if (!btn || !btn.dataset.globalSpinning) return;
  delete btn.dataset.globalSpinning;
  btn.disabled = false;
  if (btn.tagName === "INPUT") {
    btn.value = btn.dataset.globalOriginalValue;
    delete btn.dataset.globalOriginalValue;
  } else {
    btn.innerHTML = btn.dataset.globalOriginalHtml;
    delete btn.dataset.globalOriginalHtml;
  }
}

// Spin on submit — fires for ALL forms (Turbo and non-Turbo alike).
// Using capture phase so it runs before Turbo intercepts the event.
// We store the clicked submitter on the form element so turbo:submit-end
// can restore exactly the right button even when multiple submit buttons exist.
document.addEventListener("submit", (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || shouldSkipSpinner(form)) return;
  const btn = event.submitter instanceof HTMLButtonElement || event.submitter instanceof HTMLInputElement
    ? event.submitter
    : form.querySelector("button[type='submit'], input[type='submit']");
  if (btn) {
    form._globalSpinnerBtn = btn;
    spinButton(btn);
  }
}, true);

// Restore button after a Turbo form completes (success or error).
// For non-Turbo forms the page navigates away so no restore is needed.
document.addEventListener("turbo:submit-end", (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || shouldSkipSpinner(form)) return;
  const btn = form._globalSpinnerBtn ||
    event.detail?.formSubmission?.submitter ||
    form.querySelector("button[type='submit'], input[type='submit']");
  restoreButton(btn);
  delete form._globalSpinnerBtn;
});

// ─── Global nav-link spinner ──────────────────────────────────────────────────
// When any anchor tag triggers a Turbo page visit, show an inline spinner on
// the clicked link so the user has immediate feedback that navigation is
// underway.  The spinner is removed once the new page has loaded.
// Links with data-no-nav-spinner="true" or data-turbo="false" are skipped.

const NAV_SPINNER_SVG = `<svg class="animate-spin inline h-3.5 w-3.5 ml-1.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`;

let _navSpinnerLink = null;

function spinNavLink(link) {
  if (!link || link.dataset.noNavSpinner === "true" || link.dataset.turbo === "false") return;
  _navSpinnerLink = link;
  link.dataset.navSpinning = "true";
  // Append spinner without changing existing text/icons
  const span = document.createElement("span");
  span.id = "__nav_spinner__";
  span.innerHTML = NAV_SPINNER_SVG;
  link.appendChild(span);
}

function restoreNavLink() {
  if (_navSpinnerLink) {
    const spinner = _navSpinnerLink.querySelector("#__nav_spinner__");
    if (spinner) spinner.remove();
    delete _navSpinnerLink.dataset.navSpinning;
    _navSpinnerLink = null;
  }
  // Safety net: remove any orphaned spinners
  document.querySelectorAll("#__nav_spinner__").forEach((el) => el.remove());
}

document.addEventListener("click", (event) => {
  const link = event.target.closest("a[href]");
  if (!link) return;
  const href = link.getAttribute("href");
  // Skip anchor-only, javascript:, mailto:, tel: and external links
  if (!href || href.startsWith("#") || href.startsWith("javascript:") ||
      href.startsWith("mailto:") || href.startsWith("tel:")) return;
  if (link.dataset.turbo === "false" || link.dataset.noNavSpinner === "true") return;
  // Skip links that open in a new tab/window — no turbo:load fires in the current page
  if (link.target && link.target !== "" && link.target !== "_self") return;
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
  spinNavLink(link);
}, true);

document.addEventListener("turbo:load", restoreNavLink);
document.addEventListener("turbo:render", restoreNavLink);
// Restore on back/forward or if Turbo cancels the visit
document.addEventListener("turbo:before-cache", restoreNavLink);

// ─── Close Bulk Tags modal on successful Turbo form submissions ───────────────
document.addEventListener("turbo:submit-end", (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  const action = form.action || "";
  const isBulkTagsForm = action.includes("/batch_generate") || action.includes("/generate_for_assets");
  if (!isBulkTagsForm) return;
  if (event.detail?.success) {
    const modal = document.getElementById("bulkTagsModal");
    if (modal) {
      modal.classList.add("hidden");
    }
  }
});
